/**
 * Dropdown menu for selecting permission/approval mode.
 * Uses shared PERMISSION_MODES config for consistency with TaskConfirmationModal.
 */

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { InputGroupButton } from "@/components/ui/input-group";
import { PERMISSION_MODES, type PermissionMode } from "@/constants/permissions";

interface PermissionsDropdownProps {
	value: PermissionMode;
	onChange: (value: PermissionMode) => void;
	disabled?: boolean;
}

export function PermissionsDropdown({ value, onChange, disabled }: PermissionsDropdownProps) {
	const current = PERMISSION_MODES.find((m) => m.value === value) ?? PERMISSION_MODES[0];
	const CurrentIcon = current.icon;

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Tab" && e.shiftKey) {
			e.preventDefault();
			const idx = PERMISSION_MODES.findIndex((m) => m.value === value);
			onChange(PERMISSION_MODES[(idx + 1) % PERMISSION_MODES.length].value);
		}
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild disabled={disabled}>
				<InputGroupButton variant="ghost" type="button" title={current.label}>
					<CurrentIcon className="size-4" />
				</InputGroupButton>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				side="top"
				align="start"
				className="w-56 [--radius:0.95rem]"
				onKeyDown={handleKeyDown}
			>
				<DropdownMenuLabel className="flex items-center justify-between">
					<span>Permissions</span>
					<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
						<kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
							<span className="text-xs">⇧</span>⇥
						</kbd>
						<span>to switch</span>
					</div>
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<DropdownMenuRadioGroup value={value} onValueChange={(v) => onChange(v as PermissionMode)}>
					{PERMISSION_MODES.map((mode) => (
						<DropdownMenuRadioItem key={mode.value} value={mode.value}>
							<mode.icon className="size-4 text-muted-foreground" />
							{mode.label}
						</DropdownMenuRadioItem>
					))}
				</DropdownMenuRadioGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
